<template>
  <div
    ref="cardRef"
    class="character-card xmov-interactive-card"
    :class="{
      'character-card--mobile-preview-first': mobilePreviewFirst,
    }"
    :data-buried="`CharacterCard`"
    :data-buried-params="{
      character_id: data?.id,
    }"
    @mouseenter.self="onMouseenter"
    @mouseleave.self="onMouseleave"
  >
    <div class="character-card__content">
      <div class="character-card__tag">
        <XTag v-if="data?.is_hot" type="hot" size="large" />
        <XTag v-if="data?.is_new" type="new" size="large" />
      </div>

      <a :href="jumpUrl" @click.prevent="goPerson" class="character-card__link">
        <div class="character-card__image-wrapper">
          <Resource
            :src="data.cover_url"
            class="character-card__image"
            :resource-option="{
              format: 'webp',
              resize: {
                w: 500,
                m: 'lfit',
              },
            }"
          />
        </div>
        <Resource
          v-if="showVideo"
          ref="previewVideoRef"
          :src="showVideo ? data.preview_videos?.[0] : ''"
          autoplay
          loop
          muted
          playsinline
          :videoStartTime="getVideoStartTime(data, data.preview_videos?.[0])"
          :class="[
            'character-card__video',
            {
              'character-card__video--mobile-preview-first': mobilePreviewFirst,
            },
          ]"
          @play="onPreviewVideoPlay"
          @pause="onPreviewVideoPause"
        />
      </a>

      <div v-if="preview" class="character-card__preview pc-only">
        <Preview :data="data" @createOps="type => createOps(type, data)" />
      </div>
      <div class="character-card__actions">
        <div v-if="collect" class="pc-only">
          <Collect
            class="character-card__collect"
            :model-value="data?.is_star_personal"
            :data="data"
            :params="{
              model_id: data?.id || -1,
              resource_type: RESOURCE_TYPE.PERSON,
              model_type: COLLECTION_MAP.SOURCE, //后端没有类型的概念，类型松散，该字段有可能不存在
              source: COLLECTION_SOURCE.YOUYAN,
            }"
            @update:model-value="v => $emit('change', ['is_star_personal', v])"
          />
        </div>
        <TemplateShare
          v-if="share"
          :isCard="true"
          :title="$t('fen-xiang-3d-shu-zi-ren')"
          :trackParams="{
            source: resourceLocation?.source,
            id: data.id,
            tags: data.tags,
            look_name: data.look_name,
            name: data.name,
          }"
          :eventName="TRACK_EVENT.YOUYAN_PLATFORM_SHARE_CHARACTER"
          :params="{
            share_ids: [data?.id],
            model_type: COLLECTION_MAP.SOURCE,
          }"
          :shareToInnerUser="false"
          :linkUrl="`${location?.origin}/3d-avatar/${data.identity}.html`"
        ></TemplateShare>
      </div>
      <div class="character-card__badge">
        <UpgradeBadge :config_type="data?.config_type" :tab="RIGHTS_TIER_MODAL_TAB.CHARACTER" />
      </div>
    </div>
    <div v-if="action" class="pc-only">
      <div class="character-card__footer">
        <a-button
          v-if="props.data.config?.is_special_outfit === true"
          class="character-card__button character-card__button--special"
          type="primary"
          :disabled="true"
        >
          <LimitedTimeTag :user="userInfo" />
          <AiSvg class="character-card__button-icon" />
          {{ $t('bian-ji-ren-wu') }}
        </a-button>
        <CardAction
          v-else-if="!isChinese()"
          class="character-card__card-action"
          :showLeft="hasActionPermission(GENERIC_ACTION_KEYS.AVATAR_EDIT) && !is2DHuman"
          :leftTip="$t('bian-ji-ren-wu')"
          :rightText="$t('yong-ta-chuang-zuo')"
          @click-left="createOps('image', data)"
          @click-right="createOps('person', data)"
        >
          <template #leftIcon>
            <iconpark-icon name="edit"></iconpark-icon>
          </template>
        </CardAction>
        <template v-else>
          <UpgradeContent
            :config_type="data?.config_type"
            class="character-card__button character-card__button--left"
          >
            <a-button
              class="character-card__button character-card__button--left"
              @click.stop="createOps('person', data)"
            >
              <Img :src="AiColorfulSvg" class="character-card__button-img" />
              {{ $t('yong-ta-chuang-zuo') }}
            </a-button>
          </UpgradeContent>
          <a-button
            type="primary"
            v-if="hasActionPermission(GENERIC_ACTION_KEYS.AVATAR_EDIT) && !is2DHuman"
            class="character-card__button character-card__button--right"
            @click.stop="createOps('image', data)"
          >
            <LimitedTimeTag :config_type="data?.config_type" />
            <AiSvg class="character-card__button-icon" />
            {{ $t('bian-ji-ren-wu') }}
          </a-button>
        </template>
      </div>
    </div>
    <div class="character-card__info">
      <div class="character-card__name">
        {{ data.name }}
      </div>
      <span
        v-if="tagArr?.length"
        class="character-card__tag-label"
        :style="{
          color: tagColor[tagArr[0].outer_id!]?.color,
          background: tagColor[tagArr[0].outer_id!]?.bg,
        }"
      >
        {{ tagArr[0].tag_name }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBreakpoint } from '@rendora/design/index';
import Collect from '@/features/collect/index.vue';
import { TemplateShare } from '@/features/template/components/templateShare';
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import LimitedTimeTag from '@rendora/common/features/limited-time-tag/index.vue';
import UpgradeBadge from '@rendora/common/features/upgrade-badge/index.vue';
import { RIGHTS_TIER_MODAL_TAB } from '@rendora/common/types/enums';
import { is2DCharacter } from '@rendora/common/utils/is';
import { kebabCase } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import type { Character } from '~/api/interface/character';
import AiColorfulSvg from '~/assets/svg/ai-colorful.svg?url';
import AiSvg from '~/assets/svg/ai.svg';
import CardAction from '~/components/card-action/index.vue';
import UpgradeContent from '~/components/upgrade-content/index.vue';
import { SESSION_KEY } from '~/constants/config';
import { useCharacterCreateOps } from '~/hooks/character/index';
import { isChinese } from '~/utils/area';
import { COLLECTION_MAP, COLLECTION_SOURCE, RESOURCE_TYPE } from '~/utils/constant';
import { TRACK_EVENT } from '~/utils/track';
import Preview from './preview.vue';

const { t: $t } = useI18n();

const userInfo = useUserInfo();

const emit = defineEmits(['change', 'mobile-preview-start']);

const props = withDefaults(
  defineProps<{
    data: Character;
    collect?: boolean;
    share?: boolean;
    preview?: boolean;
    action?: boolean;
    target?: '_blank' | '_parent' | '_self' | '_top' | string;
    mobilePreviewFirst?: boolean;
    activeMobilePreviewId?: number | null;
  }>(),
  {
    collect: true,
    share: false,
    action: true,
    preview: true,
    mobilePreviewFirst: false,
    activeMobilePreviewId: null,
  },
);

const { hasActionPermission } = usePageAndActionPermissionData();
const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const tags = useCharacterTags();
const resourceLocation = injectResourceLocation();
const cardRef = ref<HTMLElement | null>(null);
const showVideo = ref(false);
const previewVideoRef = ref<{ play?: () => void; pause?: () => void } | null>(null);
const mobilePreviewPlaying = ref(false);
const mouseover = ref(false);

const { createOps } = useCharacterCreateOps();
let timer: any = null;
let intersectionObserver: IntersectionObserver | null = null;

const jumpUrl = computed(() => {
  return `/3d-avatar/${kebabCase(props.data.name)}-${props.data?.identity}.html`;
});

const is2DHuman = computed(() => {
  return props.data.config?.xhuman_type && is2DCharacter(props.data.config?.xhuman_type);
});

/** 暂时放在这里，后面如果需要点击tag跳转用得着 */
const rightsTag = computed(() => {
  return tags.value.tags.find(item => item.outer_id === 'character_rights');
});

const tagArr = computed(() => {
  if (rightsTag.value) {
    const childTagIdArr = props.data.tag_ids[rightsTag.value?.tag_id];
    if (!childTagIdArr) {
      return [];
    }
    return rightsTag.value.children?.filter(child => {
      return childTagIdArr?.includes(child.tag_id);
    });
  }

  return [];
});

const tagColor: any = {
  character_person: { bg: '#B9FCFF', color: '#00C5CD' },
  character_enterprise: { bg: '#D5D2FF', color: '#766FFF' },
};

const location = computed(() => {
  if (import.meta.client) {
    return window.location;
  }

  return null;
});

const onMouseenter = () => {
  if (isMobile.value) return;
  mouseover.value = true;
  timer = setTimeout(() => {
    showVideo.value = true;
  }, 200);
};

const onMouseleave = () => {
  if (isMobile.value) return;
  showVideo.value = false;
  mouseover.value = false;
  clearTimeout(timer);
  timer = null;
};

function addTrackerPoint() {
  if (resourceLocation?.source) {
    sessionSetItem(SESSION_KEY.CHARACTER_SOURCE, resourceLocation.source);
  }
}

const onPreviewVideoPlay = () => {
  mobilePreviewPlaying.value = true;
  emit('mobile-preview-start', props.data.id);
};

const onPreviewVideoPause = () => {
  mobilePreviewPlaying.value = false;
};

const startMobilePreview = async () => {
  if (!props.mobilePreviewFirst || !isMobile.value || !props.data.preview_videos?.[0]) {
    return false;
  }

  showVideo.value = true;
  await nextTick();

  try {
    await previewVideoRef.value?.play?.();
  } catch {
    mobilePreviewPlaying.value = false;
  }

  return true;
};

const resetMobilePreview = () => {
  previewVideoRef.value?.pause?.();
  showVideo.value = false;
  mobilePreviewPlaying.value = false;
};

const goPerson = async () => {
  if (props.mobilePreviewFirst && isMobile.value && !mobilePreviewPlaying.value) {
    const started = await startMobilePreview();
    if (started) {
      return;
    }
  }

  addTrackerPoint();
  navigateTo(jumpUrl.value, {
    open: {
      target: props?.target || '_blank',
    },
  });
};

onMounted(() => {
  if (!props.mobilePreviewFirst || !isMobile.value || !cardRef.value || !import.meta.client) {
    return;
  }

  intersectionObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) {
        resetMobilePreview();
      }
    },
    {
      threshold: 0.2,
    },
  );

  intersectionObserver.observe(cardRef.value);
});

onUnmounted(() => {
  clearTimeout(timer);
  intersectionObserver?.disconnect();
  intersectionObserver = null;
  resetMobilePreview();
});

watch(
  () => props.activeMobilePreviewId,
  activeId => {
    if (!props.mobilePreviewFirst || !isMobile.value) return;
    if (activeId === null || activeId === props.data.id) return;

    resetMobilePreview();
  },
);

function getVideoStartTime(avatar: Character, src?: string) {
  if (src?.endsWith('new_face.mp4')) {
    return avatar.config?.gender === 'Male' ? 3 : 11;
  }
  return 0;
}
</script>

<style lang="scss" scoped>
// Block
.character-card {
  border-radius: 12px;
  background-color: var(--x-back-1);
  display: flex;
  flex-direction: column;
  position: relative;
}

// Element: content
.character-card__content {
  flex: 1;
  background-color: var(--x-back-3);
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

// Element: tag
.character-card__tag {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 4px;
}

.character-card__badge {
  position: absolute;
  right: 8px;
  bottom: 8px;
}

// Element: link
.character-card__link {
  display: block;
}

// Element: image-wrapper
.character-card__image-wrapper {
  padding-top: 131%;
  position: relative;
}

// Element: image
.character-card__image {
  object-fit: cover;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

// Element: video
.character-card__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

// Element: preview
.character-card__preview {
  position: absolute;
  top: 9px;
  left: 9px;
}

// Element: actions
.character-card__actions {
  gap: 12px;
  position: absolute;
  top: 9px;
  right: 9px;
  display: none;
}

// Element: footer
.character-card__footer {
  width: 100%;
  padding-top: 12px;
  display: none;
  align-items: center;
  transition: bottom 0.12s ease-out;
  gap: 8px;
}

// Element: button
.character-card__button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: 8px;
}

// Modifier: button--left
.character-card__button--left {
  font-weight: 500;
  flex: 98;
  padding-left: 0;
  padding-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Modifier: button--right
.character-card__button--right {
  font-weight: 500;
  flex: 110;
  padding-left: 0;
  padding-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Element: button-icon
.character-card__button-icon {
  font-size: 16px;
  margin-right: 4px;
}

.character-card__button-img {
  width: 16px;
  height: 16px;
  margin-right: 4px;
}

// Element: card-action
.character-card__card-action {
  width: 100%;
}

// Element: info
.character-card__info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  flex-shrink: 0;
  height: 32px;
}

// Element: name
.character-card__name {
  font-size: 14px;
  color: var(--x-text-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  font-weight: bold;
}

// Element: tag-label
.character-card__tag-label {
  border-radius: 10px;
  padding: 3px 8px;
  display: inline-block;
  font-size: 12px;
  line-height: 1;
}

// Hover state
.character-card:hover .character-card__content .character-card__tag,
.character-card:hover .character-card__info {
  display: none;
}

.character-card:hover .character-card__actions,
.character-card:hover .character-card__footer {
  display: flex;
}

// .character-card:hover .character-character-card__info {
//   // bottom: 18px;
//   // visibility: visible;
// }
</style>
